import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UserPen } from 'lucide-react';
import { FormDialog, FormSection, FormTextField } from '@/components/common/FormDialog';
import { useUpdateEntryTeacherName } from '../hooks/useUpdateEntryTeacherName';
import {
  teacherNameFormSchema,
  type SpecimenInstitutionRow,
  type TeacherNameFormValues,
} from '../types/product.types';

interface TeacherNameFormModalProps {
  entry: SpecimenInstitutionRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeacherNameFormModal({ entry, open, onOpenChange }: TeacherNameFormModalProps) {
  const form = useForm<TeacherNameFormValues>({
    resolver: zodResolver(teacherNameFormSchema),
    defaultValues: { teacherName: '' },
  });

  useEffect(() => {
    form.reset({ teacherName: entry?.teacherName ?? '' });
  }, [entry, open, form]);

  const update = useUpdateEntryTeacherName(() => onOpenChange(false));

  function submit(values: TeacherNameFormValues) {
    if (entry === null) return;
    update.mutate({
      entryId: entry.detailId,
      payload: { entryKind: entry.entryKind, teacherName: values.teacherName },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={UserPen}
      title="Edit Teacher Name"
      description="The new name shows on this entry only."
      form={form}
      isPending={update.isPending}
      isEditMode
      onSubmit={submit}
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="teacherName"
          label="Teacher Name"
          placeholder="Abdul Karim"
          isFullRow
        />
      </FormSection>
    </FormDialog>
  );
}
